/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState } from "react";
import { ArmcloudEngine, KeyboardMode } from "armcloud-rtc";
import {
	AppWindow,
	ArrowLeft,
	Home,
	ImagePlus,
	Loader2,
	RefreshCw,
	ShieldAlert,
	Smartphone,
	Square,
	Wifi,
	WifiOff,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
	getStreamToken,
	releaseStreamToken,
	releaseStreamTokenKeepalive,
	heartbeatStreamToken,
	forceReleaseDeviceLock,
	sendDeviceKey,
	uploadFileToDevice,
	type StreamToken,
} from "@/services/vmos-api";
import { cn } from "@/lib/utils";

const HEARTBEAT_MS = 30_000;
// Mirrors the backend ceiling in the group control router.
const MAX_UPLOAD_BYTES = 512 * 1024 * 1024;
const MEDIA_EXT_RE = /\.(png|jpe?g|gif|webp|heic|heif|bmp|mp4|mov|m4v|webm|avi|mkv)$/i;

// SDK sendCommand keys + matching Android keycodes for the backend fallback.
const NAV_BUTTONS: {
	label: string;
	cmd: "back" | "home" | "menu";
	keycode: number;
	Icon: typeof ArrowLeft;
}[] = [
	{ label: "Back", cmd: "back", keycode: 4, Icon: ArrowLeft },
	{ label: "Home", cmd: "home", keycode: 3, Icon: Home },
	{ label: "Recents", cmd: "menu", keycode: 187, Icon: AppWindow },
];

type Status = "connecting" | "connected" | "error";

export function CloudPhoneStream({
	padId,
	deviceName,
	onClose,
}: {
	padId: string;
	deviceName: string;
	onClose: () => void;
}) {
	const viewId = useMemo(
		() => `phone-box-${padId.replace(/[^a-zA-Z0-9]/g, "")}`,
		[padId]
	);
	const engineRef = useRef<any>(null);
	const tokenRef = useRef<StreamToken | null>(null);
	// Invalidates late async results from a superseded effect run (StrictMode,
	// retry, device switch) so they can't clobber current state.
	const runIdRef = useRef(0);
	// Set once if the manual lock can no longer be refreshed, so we warn the
	// user instead of silently becoming uncontrollable.
	const lockLostRef = useRef(false);
	// Tracks an in-flight release so a new run waits for it before re-acquiring.
	const pendingReleaseRef = useRef<Promise<void>>(Promise.resolve());
	const [status, setStatus] = useState<Status>("connecting");
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [reloadKey, setReloadKey] = useState(0);
	// Frame aspect derived from the actual stream resolution (default 9:16).
	const [aspect, setAspect] = useState<{ w: number; h: number }>({ w: 9, h: 16 });
	const uploadInputRef = useRef<HTMLInputElement>(null);
	const [uploading, setUploading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(
		null
	);
	const [dragActive, setDragActive] = useState(false);
	// Depth counter so dragging over child elements doesn't flicker the overlay.
	const dragDepthRef = useRef(0);

	useEffect(() => {
		const runId = ++runIdRef.current;
		const isCurrent = () => runIdRef.current === runId;
		lockLostRef.current = false;
		let heartbeat: ReturnType<typeof setInterval> | null = null;

		// Temporary diagnostic: compare our frame vs the SDK's rendered <video>.
		const debugSizes = (tag: string) => {
			requestAnimationFrame(() => {
				const mount = document.getElementById(viewId);
				const video = mount?.querySelector("video") as HTMLVideoElement | null;
				console.debug(`[CloudPhoneStream:${tag}]`, {
					mount: mount
						? { w: mount.clientWidth, h: mount.clientHeight }
						: null,
					videoEl: video
						? { w: video.clientWidth, h: video.clientHeight }
						: null,
					videoIntrinsic: video
						? { w: video.videoWidth, h: video.videoHeight }
						: null,
				});
			});
		};

		const releaseCurrent = (): Promise<void> => {
			const info = tokenRef.current;
			tokenRef.current = null;
			try {
				engineRef.current?.stop();
			} catch {
				/* ignore */
			}
			engineRef.current = null;
			if (info) {
				const p = releaseStreamToken(padId, {
					token: info.token,
					lock_token: info.lock_token,
				})
					.then(() => {})
					.catch(() => {});
				pendingReleaseRef.current = p;
			}
			return pendingReleaseRef.current;
		};

		const onPageHide = () => {
			const info = tokenRef.current;
			if (info) {
				releaseStreamTokenKeepalive(padId, {
					token: info.token,
					lock_token: info.lock_token,
				});
			}
		};
		window.addEventListener("pagehide", onPageHide);

		const start = async () => {
			setStatus("connecting");
			setError(null);
			setBusy(false);

			// Ensure any prior run's release has completed before we acquire,
			// so we never 409 against our own in-flight lock.
			await pendingReleaseRef.current;
			if (!isCurrent()) return;

			try {
				const info = await getStreamToken(padId);
				if (!isCurrent()) {
					// A newer run took over; drop what we just acquired.
					releaseStreamToken(padId, {
						token: info.token,
						lock_token: info.lock_token,
					}).catch(() => {});
					return;
				}
				tokenRef.current = info;

				const engine: any = new ArmcloudEngine({
					baseUrl: info.base_url,
					token: info.token,
					viewId,
					deviceInfo: {
						padCode: info.pad_code,
						userId: info.user_id,
						keyboard: KeyboardMode.PAD,
						saveCloudClipboard: true,
					},
					callbacks: {
						onInit: ({ code, msg }: any) => {
							if (!isCurrent()) return;
							if (Number(code) === 0) {
								// `isSupported` is a STATIC on the SDK class in 1.6.x;
								// the instance method does not exist. Guard either.
								const supported =
									typeof (ArmcloudEngine as any).isSupported === "function"
										? (ArmcloudEngine as any).isSupported()
										: true;
								if (!supported) {
									setStatus("error");
									setError("This browser does not support RTC streaming.");
									return;
								}
								try {
									engineRef.current?.start();
									debugSizes("onInit-start");
								} catch (e: any) {
									setStatus("error");
									setError(e?.message || "Failed to start the stream");
								}
							} else {
								setStatus("error");
								setError(msg || "Failed to initialize the cloud phone");
							}
						},
						onConnectSuccess: () => {
							if (isCurrent()) {
								setStatus("connected");
								setBusy(false);
							}
						},
						onConnectFail: ({ msg }: any) => {
							if (isCurrent()) {
								setStatus("error");
								setError(msg || "Connection to the device failed");
							}
						},
						onRenderedFirstFrame: (event?: any) => {
							if (!isCurrent()) return;
							if (event?.width && event?.height) {
								setAspect({ w: event.width, h: event.height });
							}
							debugSizes("first-frame");
						},
						onChangeResolution: (payload?: any) => {
							if (!isCurrent()) return;
							const to = payload?.to;
							if (to?.width && to?.height) {
								setAspect({ w: to.width, h: to.height });
							}
							debugSizes("change-resolution");
						},
						onAutoplayFailed: () => {
							try {
								engineRef.current?.startPlay();
							} catch {
								/* ignore */
							}
						},
						onAutoRecoveryTime: () => {
							try {
								engineRef.current?.start();
							} catch {
								/* ignore */
							}
						},
						onErrorMessage: (e: any) => {
							console.warn("armcloud stream error", e);
						},
					},
				});
				engineRef.current = engine;

				// Keep the short manual lock alive while the stream is open.
				heartbeat = setInterval(async () => {
					const cur = tokenRef.current;
					if (!cur || !isCurrent()) return;
					try {
						const res = await heartbeatStreamToken(padId, cur.lock_token);
						if (!res?.refreshed && !lockLostRef.current) {
							lockLostRef.current = true;
							toast.warning("Lost the device lock — reconnect to keep controlling it.");
						}
					} catch {
						if (!lockLostRef.current) {
							lockLostRef.current = true;
							toast.warning("Couldn't keep the device lock alive — reconnect to continue.");
						}
					}
				}, HEARTBEAT_MS);
			} catch (e: any) {
				if (!isCurrent()) return;
				const msg = e?.message || "Failed to open the device stream";
				setStatus("error");
				setError(msg);
				setBusy(/busy/i.test(msg));
			}
		};

		start();

		return () => {
			// Invalidate this run so late callbacks/results are ignored.
			if (runIdRef.current === runId) runIdRef.current++;
			window.removeEventListener("pagehide", onPageHide);
			if (heartbeat) clearInterval(heartbeat);
			releaseCurrent();
		};
	}, [padId, viewId, reloadKey]);

	const handleForceRelease = async () => {
		try {
			await forceReleaseDeviceLock(padId);
			setReloadKey((k) => k + 1);
		} catch (e: any) {
			setError(e?.message || "Force release failed (admin only)");
		}
	};

	// Prefer the SDK's client-side keystroke channel (sendCommand maps
	// back/home/menu -> Android keycodes 4/3/187); fall back to the backend
	// ADB keyevent only if the SDK method is unavailable.
	const sendNav = async (cmd: "back" | "home" | "menu", keycode: number) => {
		const engine = engineRef.current;
		if (engine && typeof engine.sendCommand === "function") {
			try {
				engine.sendCommand(cmd);
				return;
			} catch {
				/* fall through to backend */
			}
		}
		try {
			await sendDeviceKey(padId, keycode);
		} catch (e: any) {
			toast.error(e?.message || "Key event failed");
		}
	};

	// Push media straight into the device gallery (no content run). The device
	// scans /DCIM/ asynchronously, so items can take a few seconds to show up.
	const handleFiles = async (files: File[]) => {
		if (status !== "connected") {
			toast.error("Connect to the device first");
			return;
		}
		if (uploading) return;

		const media = files.filter(
			(f) => f.type.startsWith("image/") || f.type.startsWith("video/") || MEDIA_EXT_RE.test(f.name)
		);
		if (media.length === 0) {
			toast.error("Only images and videos can be added");
			return;
		}
		const tooBig = media.find((f) => f.size > MAX_UPLOAD_BYTES);
		if (tooBig) {
			toast.error(`${tooBig.name} exceeds the 512 MB limit`);
			return;
		}

		setUploading(true);
		setUploadProgress({ done: 0, total: media.length });
		let ok = 0;
		for (const file of media) {
			try {
				await uploadFileToDevice({ file, padId, destinationPath: "/DCIM/" });
				ok += 1;
			} catch (e: any) {
				toast.error(`${file.name}: ${e?.message || "upload failed"}`);
			}
			setUploadProgress({ done: ok, total: media.length });
		}
		setUploading(false);
		setUploadProgress(null);
		if (ok > 0) {
			toast.success(
				`Added ${ok} to the device gallery — open the app to post it (may take a few seconds to appear).`
			);
		}
	};

	const onDragEnter = (e: React.DragEvent) => {
		if (status !== "connected" || uploading) return;
		e.preventDefault();
		dragDepthRef.current += 1;
		setDragActive(true);
	};

	const onDragOver = (e: React.DragEvent) => {
		if (status !== "connected" || uploading) return;
		e.preventDefault();
		if (!dragActive) setDragActive(true);
	};

	const onDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
		if (dragDepthRef.current === 0) setDragActive(false);
	};

	const onDrop = (e: React.DragEvent) => {
		e.preventDefault();
		dragDepthRef.current = 0;
		setDragActive(false);
		if (status !== "connected" || uploading) {
			toast.error("Connect to the device first");
			return;
		}
		if (e.dataTransfer.files?.length) {
			handleFiles(Array.from(e.dataTransfer.files));
		}
	};

	return (
		<div
			className="flex flex-col h-full relative"
			onDragEnter={onDragEnter}
			onDragOver={onDragOver}
			onDragLeave={onDragLeave}
			onDrop={onDrop}
		>
			<div className="flex items-center gap-3 px-4 py-2 border-b shrink-0">
				<Smartphone className="h-4 w-4 text-primary" />
				<span className="text-sm font-medium">{deviceName}</span>
				<span className="font-mono text-[11px] text-muted-foreground">{padId}</span>
				<span
					className={cn(
						"ml-1 inline-flex items-center gap-1 text-xs",
						status === "connected"
							? "text-green-600"
							: status === "connecting"
								? "text-muted-foreground"
								: "text-destructive"
					)}
				>
					{status === "connected" ? (
						<Wifi className="h-3 w-3" />
					) : status === "connecting" ? (
						<Loader2 className="h-3 w-3 animate-spin" />
					) : (
						<WifiOff className="h-3 w-3" />
					)}
					{status}
				</span>
				<div className="ml-auto flex items-center gap-2">
					<input
						ref={uploadInputRef}
						type="file"
						accept="image/*,video/*"
						multiple
						className="hidden"
						onChange={(e) => {
							if (e.target.files?.length) handleFiles(Array.from(e.target.files));
							e.target.value = "";
						}}
					/>
					<Button
						size="sm"
						variant="outline"
						disabled={status !== "connected" || uploading}
						onClick={() => uploadInputRef.current?.click()}
						title={
							status === "connected"
								? "Add photos or videos to the device gallery"
								: "Connect to the device first"
						}
					>
						{uploading ? (
							<Loader2 className="h-3 w-3 mr-1 animate-spin" />
						) : (
							<ImagePlus className="h-3 w-3 mr-1" />
						)}
						{uploading && uploadProgress
							? `Adding ${uploadProgress.done}/${uploadProgress.total}`
							: "Add media"}
					</Button>
					<Button
						size="sm"
						variant="outline"
						onClick={() => setReloadKey((k) => k + 1)}
					>
						<RefreshCw className="h-3 w-3 mr-1" /> Reconnect
					</Button>
					<Button size="sm" variant="destructive" onClick={onClose}>
						<Square className="h-3 w-3 mr-1" /> Stop
					</Button>
				</div>
			</div>

			<div className="flex-1 min-h-0 relative flex items-center justify-center bg-background p-3">
				<div
					id={viewId}
					style={{ aspectRatio: `${aspect.w} / ${aspect.h}` }}
					className={cn(
						"relative h-full max-w-full overflow-hidden rounded-xl border border-border bg-black",
						error && "hidden"
					)}
				/>
				{status === "connecting" && !error && (
					<div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground">
						<Loader2 className="h-6 w-6 animate-spin" />
						<span className="text-xs">Connecting to device…</span>
					</div>
				)}
				{error && (
					<div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center text-foreground">
						<WifiOff className="h-7 w-7 opacity-60" />
						<p className="text-sm max-w-md">{error}</p>
						<div className="flex items-center gap-2">
							<Button size="sm" onClick={() => setReloadKey((k) => k + 1)}>
								<RefreshCw className="h-3 w-3 mr-1" /> Retry
							</Button>
							{busy && (
								<Button size="sm" variant="outline" onClick={handleForceRelease}>
									<ShieldAlert className="h-3 w-3 mr-1" /> Force release (admin)
								</Button>
							)}
						</div>
					</div>
				)}
			</div>

			<div className="shrink-0 border-t bg-background px-4 py-2 flex items-center justify-center gap-2">
				{NAV_BUTTONS.map(({ label, cmd, keycode, Icon }) => (
					<Button
						key={cmd}
						size="sm"
						variant="outline"
						onClick={() => sendNav(cmd, keycode)}
					>
						<Icon className="h-4 w-4 mr-1" /> {label}
					</Button>
				))}
			</div>

			{dragActive && (
				<div className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-2 bg-background/80 backdrop-blur-sm pointer-events-none">
					<div className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-primary px-10 py-8 text-center">
						<ImagePlus className="h-8 w-8 text-primary" />
						<p className="text-sm font-medium">Drop to add to the device gallery</p>
						<p className="text-xs text-muted-foreground">Photos and videos</p>
					</div>
				</div>
			)}
		</div>
	);
}
